/**
 * Pure rules for Training's launch screens: what a knowledge-check result says about one question,
 * what a catalog entry offers, when a certificate exists, and how the training admin's table reads.
 * Free of React so they can be tested directly (the utils/nav.ts reasoning).
 *
 * Shapes follow AVS OS docs/architecture/learning-copilot-api.md. Where the contract leaves a shape
 * to another workstream (the per-option feedback on a failed check), every form it might reasonably
 * take is read, and a missing field means "nothing to show", never an error.
 */

// ── Knowledge check feedback ─────────────────────────────────────────────────────────────────

export type QuestionFeedback = {
  /** true / false once graded; null when the result doesn't say. */
  correct: boolean | null;
  /** Why the right answer is right. */
  explanation: string | null;
  /** What the author wrote about the option this learner picked. */
  chosenFeedback: string | null;
  /** The right option, only when AVS OS revealed it (a pass, or a lesson already passed). */
  correctIndex: number | null;
};

const text = (v: unknown): string | null => (typeof v === "string" && v.trim() ? v.trim() : null);

/** Per-option feedback in any of the forms it may arrive in: an array aligned with the options, a map by index, or one string. */
function pickOptionFeedback(source: any, selectedIndex: number | null): string | null {
  if (!source) return null;
  if (typeof source === "string") return text(source);
  if (selectedIndex === null || selectedIndex === undefined) return null;
  if (Array.isArray(source)) return text(source[selectedIndex]);
  if (typeof source === "object") return text(source[selectedIndex]) ?? text(source[String(selectedIndex)]);
  return null;
}

function pickCorrectIndex(...sources: any[]): number | null {
  for (const s of sources) {
    if (!s) continue;
    if (Number.isInteger(s.correctIndex)) return s.correctIndex;
    if (Array.isArray(s.correctOptionIds) && s.correctOptionIds.length === 1 && Number.isInteger(s.correctOptionIds[0])) {
      return s.correctOptionIds[0];
    }
  }
  return null;
}

/**
 * What to show under one question after a submission.
 *
 * Reads `result.questions[]` (one per question, matched by checkId) and `result.explanations[checkId]`
 * — the pre-launch shape, still sent on a pass — and lets either carry `explanation`,
 * `optionFeedback` / `feedback` / `selectedFeedback`, and `correctIndex` / `correctOptionIds`.
 */
export function questionFeedback(result: any, checkId: string, selectedIndex: number | null): QuestionFeedback {
  const empty: QuestionFeedback = { correct: null, explanation: null, chosenFeedback: null, correctIndex: null };
  if (!result || typeof result !== "object") return empty;
  const q = Array.isArray(result.questions) ? result.questions.find((x: any) => x?.checkId === checkId) : null;
  const key = result.explanations && typeof result.explanations === "object" ? result.explanations[checkId] : null;
  const fb = result.feedback && typeof result.feedback === "object" ? result.feedback[checkId] : null;

  return {
    correct: typeof q?.correct === "boolean" ? q.correct : null,
    explanation: text(q?.explanation) ?? text(key?.explanation) ?? text(fb?.explanation),
    chosenFeedback:
      text(q?.selectedFeedback) ??
      pickOptionFeedback(q?.optionFeedback ?? q?.feedback, selectedIndex) ??
      pickOptionFeedback(key?.optionFeedback, selectedIndex) ??
      pickOptionFeedback(fb?.optionFeedback ?? fb, selectedIndex),
    correctIndex: pickCorrectIndex(q, key, fb),
  };
}

/** The pass mark to show: the lesson's own, else the result's, else AVS OS's default of 80. */
export function passMarkFor(lesson: any, result?: any): number {
  for (const v of [result?.passMark, lesson?.passMark]) {
    if (typeof v === "number" && Number.isFinite(v) && v > 0 && v <= 100) return Math.round(v);
  }
  return 80;
}

// ── Lesson extras ────────────────────────────────────────────────────────────────────────────

export type Caption = { language: string; label: string; url: string };

/** Captions safe to load: https only, with a language code. Anything else is dropped. */
export function usableCaptions(captions: unknown): Caption[] {
  if (!Array.isArray(captions)) return [];
  return captions.filter(
    (c: any) =>
      c &&
      typeof c.url === "string" &&
      /^https:\/\//i.test(c.url) &&
      typeof c.language === "string" &&
      /^[a-z]{2,3}(-[A-Za-z0-9]{2,8})*$/.test(c.language)
  ).map((c: any) => ({ language: c.language, label: text(c.label) || c.language, url: c.url }));
}

/** Objectives as a clean list (null, junk and blanks dropped). */
export function objectivesOf(lesson: any): string[] {
  return Array.isArray(lesson?.objectives) ? lesson.objectives.map(text).filter(Boolean) as string[] : [];
}

export type QuickRefGroup = { title: string | null; items: string[] };

/** Quick reference groups. An item may be a string or `{label, value}` / `{term, detail}`. */
export function quickReferenceOf(lesson: any): QuickRefGroup[] {
  if (!Array.isArray(lesson?.quickReference)) return [];
  return lesson.quickReference
    .map((g: any) => {
      const items = (Array.isArray(g?.items) ? g.items : [])
        .map((i: any) => {
          if (typeof i === "string") return text(i);
          const a = text(i?.label ?? i?.term ?? i?.title);
          const b = text(i?.value ?? i?.detail ?? i?.text);
          return a && b ? `${a}: ${b}` : a || b;
        })
        .filter(Boolean);
      return { title: text(g?.title), items };
    })
    .filter((g: QuickRefGroup) => g.items.length > 0);
}

// ── Catalog (View All) ───────────────────────────────────────────────────────────────────────

export type EnrollOffer = "enrolled" | "enroll" | "ask";

/**
 * What a catalog entry offers: already on it (open it), enrol yourself, or — while self-enrolment is
 * off — nothing to click (ask your training admin). A course reached through a path counts as on it.
 */
export function enrollOffer(entry: any, selfEnrollEnabled: boolean): EnrollOffer {
  if (entry?.enrolled || entry?.onPath) return "enrolled";
  return selfEnrollEnabled ? "enroll" : "ask";
}

/** Certificates exist for finished courses (contract: lessons > 0 and every one completed). */
export function hasCertificate(course: any): boolean {
  return Number(course?.lessons) > 0 && course?.completed === course?.lessons;
}

// ── Training admin ───────────────────────────────────────────────────────────────────────────

const STATUS_WORDS: Record<string, string> = {
  assigned: "Not started",
  in_progress: "In progress",
  completed: "Complete",
};

export function statusWord(status: string | null | undefined): string {
  return (status && STATUS_WORDS[status]) || "Not started";
}

/** One learner's summary line for the team table: courses done of courses, and anything overdue. */
export function learnerSummary(learner: any): { done: number; total: number; overdue: number } {
  const courses = Array.isArray(learner?.courses) ? learner.courses : [];
  return {
    done: courses.filter((c: any) => c?.status === "completed").length,
    total: courses.length,
    overdue: courses.filter((c: any) => c?.overdue && c?.status !== "completed").length,
  };
}

/** Basic shape check before the add-person form calls the server, which checks it properly. */
export function validatePerson(p: { email?: string; firstName?: string; lastName?: string }): string | null {
  const email = (p.email || "").trim();
  if (!/^[^\s@<>,;]+@[^\s@<>,;]+\.[^\s@<>,;]+$/.test(email)) return "Enter a valid work email address.";
  if (!(p.lastName || "").trim()) return "Enter a last name.";
  if ((p.firstName || "").length > 80 || (p.lastName || "").length > 80) return "Names must be 80 characters or fewer.";
  return null;
}
